export type InfeasibilitySeverity = "ok" | "warning" | "blocking";

export interface ViolationSample {
  node_id: string;
  details: Record<string, unknown>;
  slack: number;
}

export interface PreflightIssue {
  reason_code: string;
  severity: "hard" | "blocking";
  evidence: Record<string, unknown>;
  human_message_ko: string;
  fix_suggestions_ko: string[];
}

/** 해결책을 "어디서 어떻게 고치는가" (백엔드 fix_location.py) */
export interface FixLocation {
  /** auto_apply=버튼으로 바로 재생성 / manual_edit=그 설정 화면 / manual_navigate=대상으로 이동 */
  mode: "auto_apply" | "manual_edit" | "manual_navigate";
  /** 프론트가 실제 화면으로 라우팅할 의미 키 (예: roster_config.night_month_limit) */
  where: string;
  /** 사람이 읽는 위치 경로 (브레드크럼) */
  where_label_ko: string;
  /** 무엇을 얼마로 바꾸는지 (auto_apply 만) */
  how_ko?: string | null;
  config_key?: string | null;
  /** 딥링크 컨텍스트 (간호사/날짜 등) */
  target?: Record<string, unknown> | null;
}

export interface ResolutionChange {
  config_key: string;
  label_ko?: string;
  from?: unknown;
  to?: unknown;
  suggested_value?: number | null;
}

/** 유저가 고르는 해결 옵션 카드 */
export interface ResolutionOption {
  option_id: string;
  kind?: string;
  source?: "probe" | "ontology" | string;
  /** probe 재계산으로 실제 풀림 확인됨 */
  verified?: boolean;
  title_ko: string;
  trade_off_ko?: string;
  changes?: ResolutionChange[];
  /** 그대로 /roster_create/async 의 config_override 로 재생성 */
  apply?: Record<string, unknown>;
  /** ontology 옵션(비-config-key) — config_override 대신 이걸로 재생성 */
  treatment_ids?: string[];
  /** per-nurse 주말휴무 해제 옵션 — 해당 nurse_id 들의 주말휴무를 해제(period)하고 재생성 */
  weekend_off_release?: string[];
  /** per-nurse 월 야간 한도 하향 옵션 — 해당 간호사의 n_exact/n_max 를 낮추고(NurseMonthlyLimit) 재생성 */
  monthly_limit_release?: Array<{ nurse_id: string; field: "n_exact" | "n_max"; value: number }>;
  /** per-nurse 금지근무(banned-OFF) 해제 옵션 — 겹치는 날의 BannedWantedEntry.is_applied=False 후 재생성 */
  banned_wanted_release?: Array<{ nurse_id: string; days: number[] }>;
  /** per-nurse 근무유형 추가 옵션 — allowed_shifts 에 D/E 등을 합집합(period)하고 재생성 */
  allowed_shift_add?: Array<{ nurse_id: string; add: string[] }>;
  fix?: FixLocation | null;
}

/** 데이터성 원인 (이동해서 직접 수정) */
export interface ResolutionCause {
  reason_code?: string;
  human_message_ko?: string;
  fix?: FixLocation | null;
}

/** 모달 chrome(제목·섹션 헤더·버튼) 문구 — 백엔드 단일 소스. 없으면 프론트 기본값 폴백. */
export interface InfeasibilityUiText {
  title?: string;
  description?: string;
  options_header?: string;
  verified_badge?: string;
  apply_button?: string;
  manual_after_edit?: string;
  causes_header?: string;
  issues_header?: string;
  suggestions_header?: string;
  close_button?: string;
}

export interface Infeasibility {
  severity: InfeasibilitySeverity;
  /** ★ 모달 chrome 문구(백엔드 제공, 없으면 기본값) */
  ui_text?: InfeasibilityUiText;
  summary_message_ko: string;
  preflight_issues: PreflightIssue[];
  applied_relaxations: string[];
  fix_suggestions_ko: string[];
  /** ★ 유저가 고를 해결 옵션 (원클릭 재생성 / 이동 수정) */
  resolution_options?: ResolutionOption[];
  /** ★ 데이터성 원인 + 수정 위치 */
  causes?: ResolutionCause[];
  violation_summary: Partial<
    Record<
      "grade_min" | "grade_max" | "team_min" | "coverage_min",
      { count: number; samples: ViolationSample[] }
    >
  >;
  last_error_reason: string | null;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object";
}

export function isInfeasibility(value: unknown): value is Infeasibility {
  return (
    isRecord(value) &&
    (value.severity === "ok" ||
      value.severity === "warning" ||
      value.severity === "blocking")
  );
}

export function getInfeasibilityFromResponseBody(value: unknown) {
  if (!isRecord(value)) return null;

  if (isInfeasibility(value.infeasibility)) return value.infeasibility;

  const detail = value.detail;
  if (isRecord(detail) && isInfeasibility(detail.infeasibility)) {
    return detail.infeasibility;
  }

  return null;
}

export function getInfeasibilityFromError(error: unknown) {
  if (!isRecord(error)) return null;

  const response = error.response;
  if (!isRecord(response)) return null;

  return getInfeasibilityFromResponseBody(response.data);
}

function normalizePythonDictString(value: string) {
  return value
    .replace(/\bNone\b/g, "null")
    .replace(/\bTrue\b/g, "true")
    .replace(/\bFalse\b/g, "false")
    .replace(/'/g, '"');
}

function extractObjectLikeString(value: string) {
  const startIndex = value.indexOf("{");
  const endIndex = value.lastIndexOf("}");
  if (startIndex < 0 || endIndex <= startIndex) return null;
  return value.slice(startIndex, endIndex + 1);
}

function getFallbackSummary(value: string) {
  const match = /["']summary_message_ko["']\s*:\s*["']([^"']+)["']/.exec(value);
  return match?.[1] ?? "";
}

export function getInfeasibilityFromUnknown(value: unknown) {
  if (isInfeasibility(value)) return value;

  const fromBody = getInfeasibilityFromResponseBody(value);
  if (fromBody) return fromBody;

  if (typeof value !== "string") return null;

  const objectLike = extractObjectLikeString(value);
  if (!objectLike) return null;

  const parseCandidates = [objectLike, normalizePythonDictString(objectLike)];

  for (const candidate of parseCandidates) {
    try {
      const parsed = JSON.parse(candidate) as unknown;
      if (isInfeasibility(parsed)) return parsed;

      const infeasibility = getInfeasibilityFromResponseBody(parsed);
      if (infeasibility) return infeasibility;
    } catch {
      // Try the next serialization form.
    }
  }

  const summaryMessage = getFallbackSummary(objectLike);
  if (!summaryMessage) return null;

  return {
    severity: "blocking",
    summary_message_ko: summaryMessage,
    preflight_issues: [],
    applied_relaxations: [],
    fix_suggestions_ko: [],
    violation_summary: {},
    last_error_reason: value,
  } satisfies Infeasibility;
}
